import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, Input, LOCALE_ID, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, CommandBarComponent, IconCommand } from "@tylertech/commands-module";
import { BusyIndicatorComponent, formatAbbreviatedNumber, TruncateTextComponent } from "@tylertech/common-components-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ForgeCardModule, ForgeIconButtonModule, ForgeIconModule, ForgeToolbarModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { Utilities } from "@tylertech/shared-module";
import { IChartCriteria } from "@tylertech/tyler-api-types";
import { ChartConfiguration, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

interface IProjectDetail
{
    ProjectId: string;
    ProjectName: string;
    ProjectNumber: string;
    TotalActivity: number;
    TotalBudget: number;
    YtdActivity: number;
}

@Component({
    selector: "project-accounting-project-budget-activity-chart",
    templateUrl: "./projectBudgetActivityChart.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        ChartJsContainerComponent,
        CommandBarComponent,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
        TruncateTextComponent,
    ]
})
export class ProjectBudgetActivityChartComponent implements OnDestroy, OnInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $fileService = inject(FileService);
    private readonly $chartService = inject(ChartService);

    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;
    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"bar">;

    private _chartCriteria: IChartCriteria;
    isLoaded: boolean;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.hasData(),
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.chartCriteria?.ChartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartTitle(): string
    {
        return "Budget/Activity by Project";
    }

    get ChartSubtitle(): string
    {
        if (!this.hasChartCriteria) return null;
        const parm = this.chartCriteria.Parameters?.find(x => x.Id === "BudgetActivityByProjectFiscal");
        if (parm)
        {
            if (parm.Value) return null;
            const values = parm.Value.split(",");
            const fiscals: Array<string> = [];
            for (let i = 0, l = values.length; i < l; i++)
            {
                const v = parm.Options?.find(x => x.Value === values[i]);
                if (v)
                {
                    fiscals.push(v.Display);
                }
            }
            return fiscals.join(", ");
        }
        return null;
    }

    get hasChartCriteria(): boolean
    {
        return !!this.chartCriteria?.Parameters;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            }
            catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    chartClass(): string
    {
        return this.chartId.replace(".", "-").toLowerCase() + "-class";
    }

    hasData(): boolean
    {
        return (
            this.chartCriteria?.ChartDatas?.length > 0 &&
            !Utilities.IsNullOrUndefined(this.chartCriteria.ChartDatas[0].Data) &&
            !Utilities.IsNullOrUndefined(this.chartCriteria.ChartDatas[0].Data[0])
        );
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService
            .GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    }
                    catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService
            .showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {

        if (!this.chartCriteria.ChartDatas[0].Labels?.length)
        {
            this.chartCriteria.ChartDatas[0].Labels = ["Budget", "Activity", "YTD Activity"];
        }

        const labels = [];
        const budget = [];
        const activity = [];
        const ytd = [];
        const data = this.chartCriteria.ChartDatas[0].Data[0] as Array<IProjectDetail>;

        // TODO(DO-7754) - could use ChartJsConfigBuilder
        for (let i = 0, l = data.length; i < l; i++)
        {
            const { ProjectName, TotalBudget, TotalActivity, YtdActivity } = data[i];

            labels.push(ProjectName);

            budget.push(TotalBudget);

            activity.push(TotalActivity);

            ytd.push(YtdActivity);
        }

        const chartColors = this.$chartService.getColors(3);
        const budgetColor = chartColors[0];
        const activityColor = chartColors[1];
        const ytdColor = chartColors[2];

        this.chartConfig = {
            type: "bar",
            options: {
                maintainAspectRatio: false,
                indexAxis: 'y',
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12 },
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: TooltipItem<"bar">[]) =>
                            {
                                return this.chartCriteria.ChartDatas[0].Data[0][tooltipItems[0].dataIndex].AccountName;
                            },
                            label: (tooltipItem: TooltipItem<"bar">) =>
                            {
                                const formatted = formatCurrency(Number(tooltipItem.raw), this.locale, "$");
                                return `${this.chartCriteria.ChartDatas[0].Labels[tooltipItem.datasetIndex]}: ${formatted}`;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        type: "linear",
                        display: true,
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                    y: {
                        display: true,
                        grid: { display: true },
                        labels: labels,
                    },
                },
            },
            data: {
                labels: [],
                datasets: [
                    {
                        label: this.chartCriteria.ChartDatas[0].Labels[0],
                        data: budget,
                        backgroundColor: budgetColor,
                        borderColor: budgetColor,
                        hoverBackgroundColor: budgetColor,
                        hoverBorderColor: budgetColor,
                        xAxisID: "x-axis-1",
                        barPercentage: 0.8,
                    },
                    {
                        label: this.chartCriteria.ChartDatas[0].Labels[1],
                        data: activity,
                        backgroundColor: activityColor,
                        borderColor: activityColor,
                        hoverBackgroundColor: activityColor,
                        hoverBorderColor: activityColor,
                        barPercentage: 0.8,
                    }],
            },
        };

        if (ytd.filter(x => x).length)
        {

            this.chartConfig.data.datasets.push(
                {
                    label: this.chartCriteria.ChartDatas[0].Labels[2],
                    data: ytd,
                    backgroundColor: ytdColor,
                    borderColor: ytdColor,
                    hoverBackgroundColor: ytdColor,
                    hoverBorderColor: ytdColor,
                    barPercentage: 0.8,
                });
        }

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
